import 'antd/dist/reset.css';
import React, { useState, useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Tabs, Button, Spin } from "antd";
import { LogoutOutlined } from '@ant-design/icons';
import api from './api/axios';
import logo from './logo.jpg';
import leftImage from './diligent_hive.png';
import PurchaseInvoice from "./pages/PurchaseInvoice";
import Payments from "./pages/Payments";
import Quotation from "./pages/Quotation";
import SalesInvoice from "./pages/SalesInvoice";
import Receipts from "./pages/Receipts";
import SetupSuppliersCustomers from "./pages/SetupSuppliersCustomers";
import Reports from "./pages/Reports";
import Login from "./pages/Login";
import AdHocPayments from "./pages/AdHocPayments";
import PurchaseOrder from "./pages/PurchaseOrder";
import GenSalesInvoice from "./pages/GenSalesInvoice";
import Merge_PDF from './pages/Merge_PDF';
import Folders from './pages/Folders';
import ReminderMails from './pages/ReminderMails';
import AuditLog from './pages/AuditLog';

const tabList = [
    { key: "incoming-invoice", label: "Purchase Invoice", path: "/incoming-invoice" },
    { key: "payments", label: "Purchase Payments", path: "/payments" },
    { key: "quotation", label: "Quotation", path: "/quotation" },
    { key: "purchaseorder", label: "Purchase Order", path: "/purchaseorder" },
    { key: "outgoing-invoice", label: "Sales Invoice", path: "/outgoing-invoice" },
    { key: "remindermails", label: "ReminderMails", path: "/remindermails" },
    { key: "receipts", label: "Sales Receipts", path: "/receipts" },
    { key: "adhocpayments", label: "AdHoc Payments", path: "/adhocpayments" },
    { key: "reports", label: "Reports", path: "/reports" },
    { key: "setup", label: "Suppliers Customers PO", path: "/setup" },
    { key: "gensalesinvoice", label: "Gen Sales Invoice", path: "/gensalesinvoice" },
    { key: "mergepurchaseinvoice", label: "Merge Purchase Invoice", path: "/mergepurchaseinvoice" },
    { key: "folders", label: "Folders", path: "/folders" },
    { key: "auditlog", label: "AuditLog", path: "/auditlog" },
];

function App() {
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [loading, setLoading] = useState(true);
    const [skipVerify, setSkipVerify] = useState(false);

    useEffect(() => {
        const initAuth = async () => {
            const token = localStorage.getItem('token');
            if (!token) {
                setIsLoggedIn(false);
                setLoading(false);
                return;
            }

            // Skip verify if just logged in
            if (skipVerify) {
                setIsLoggedIn(true);
                setLoading(false);
                return;
            }

            try {
                await api.get('/api/verify');
                setIsLoggedIn(true);
            } catch {
                localStorage.removeItem('token');
                setIsLoggedIn(false);
            } finally {
                setLoading(false);
            }
        };

        initAuth();
    }, [skipVerify]);  // ✅ Depend on skipVerify

    const handleLoginSuccess = () => {
        setSkipVerify(true);  // ✅ Skip next verify
        setIsLoggedIn(true);
    };

    const handleLogout = () => {
        localStorage.removeItem('token');
        setIsLoggedIn(false);
        setSkipVerify(false);  // Reset
        window.location.href = '/login';
    };

    if (loading) {
        return <Spin center size="large" />;
    }

    if (!isLoggedIn) {
        return <Login onLoginSuccess={handleLoginSuccess} />;  // ✅ Pass callback
    }

    // TabNav Component (defined INSIDE App - fixes scope)
    const TabNav = () => {
        const location = useLocation();
        const navigate = useNavigate();
        const cleanedPath = location.pathname.replace(/\/+$/, '');
        const current = tabList.find(tab => tab.path === cleanedPath)?.key;

        React.useEffect(() => {
            if (!current) {
                navigate("/incoming-invoice", { replace: true });
            }
        }, [current, navigate]);

        if (!current) return null;

        return (
            <Tabs
                className="main-tabs"
                activeKey={current}
                onChange={key => {
                    const tab = tabList.find(t => t.key === key);
                    if (tab) navigate(tab.path);
                }}
                items={tabList.map(tab => ({
                    key: tab.key,
                    label: tab.label,
                }))}
                tabBarExtraContent={
                    <Button
                        size="small"
                        danger
                        icon={<LogoutOutlined />}
                        onClick={handleLogout}  // ✅ In scope now
                        style={{ marginLeft: 16 }}
                    >
                        Logout
                    </Button>
                }
            />
        );
    };

    return (
        <BrowserRouter>
            <div className="logo-bar" style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '10px 20px',
                background: '#fff'
            }}>
                <img src={leftImage} alt="" style={{
                    height: 100,
                    width: 'auto',
                    objectFit: 'contain',
                    maxWidth: '250px'
                }} />
                <img src={logo} alt="Company Logo" style={{
                    height: 90,
                    width: 'auto',
                    objectFit: 'contain',
                    maxWidth: '250px'
                }} />
            </div>

            <div className="app-main-container">
                <TabNav />
                <Routes>
                    <Route path="/incoming-invoice" element={<PurchaseInvoice />} />
                    <Route path="/payments" element={<Payments />} />
                    <Route path="/quotation" element={<Quotation />} />
                    <Route path="/purchaseorder" element={<PurchaseOrder />} />
                    <Route path="/outgoing-invoice" element={<SalesInvoice />} />
                    <Route path="/remindermails" element={<ReminderMails />} />
                    <Route path="/receipts" element={<Receipts />} />
                    <Route path="/adhocpayments" element={<AdHocPayments />} />
                    <Route path="/setup" element={<SetupSuppliersCustomers />} />
                    <Route path="/reports" element={<Reports />} />
                    <Route path="/folders" element={<Folders />} />
                    <Route path="/gensalesinvoice" element={<GenSalesInvoice />} />
                    <Route path="/mergepurchaseinvoice" element={<Merge_PDF />} />
                    <Route path="/auditlog" element={<AuditLog />} />
                    <Route path="/" element={<Navigate to="/incoming-invoice" replace />} />
                    <Route path="*" element={<Navigate to="/incoming-invoice" replace />} />
                </Routes>
            </div>
        </BrowserRouter>
    );
}

export default App;
